import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { COPILOT } from "@/constants/testIds";
import { fetchSuggestedQuestions, API } from "@/lib/api";
import { Sparkles, Send, MessageSquare } from "lucide-react";
import useTrackPage from "@/hooks/useTrackPage";

export default function CopilotPage() {
  useTrackPage("copilot_open");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi Avery — I'm your **KidHQ Copilot**. Ask me anything about Emma or Noah's week. Try a suggestion below or type your own." }
  ]);
  const [input, setInput] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { fetchSuggestedQuestions().then(d => setSuggested(d.questions || [])); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data:")) continue;
          const payload = JSON.parse(ev.slice(5).trim());
          if (payload.type === "delta") {
            setMessages(m => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.content };
              return copy;
            });
          } else if (payload.type === "done") {
            setSessionId(payload.session_id);
          } else if (payload.type === "error") {
            setMessages(m => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: `Sorry — ${payload.message}` };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the assistant. Try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div data-testid={COPILOT.page} className="space-y-6 max-w-4xl mx-auto">
      <header className="fcc-card p-7 flex items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[var(--fcc-ink)] text-white flex items-center justify-center"><Sparkles className="w-5 h-5" /></span>
        <div>
          <p className="label-overline">KidHQ Copilot</p>
          <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-1">Ask anything about your family's week</h1>
        </div>
      </header>

      <div data-testid={COPILOT.messages} className="fcc-card p-7 min-h-[420px] flex flex-col gap-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <span className="w-8 h-8 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center shrink-0 mt-1"><Sparkles className="w-4 h-4 text-[var(--fcc-green)]" /></span>}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-[var(--fcc-ink)] text-white" : "bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]"}`}>
              {m.role === "assistant" ? (
                <div className="copilot-markdown text-sm leading-relaxed">
                  <ReactMarkdown>{m.content || (streaming ? "…" : "")}</ReactMarkdown>
                </div>
              ) : (
                <p className="whitespace-pre-wrap font-body text-sm leading-relaxed">{m.content}</p>
              )}
            </div>
            {m.role === "user" && <span className="w-8 h-8 rounded-xl bg-[var(--fcc-emma)] flex items-center justify-center shrink-0 mt-1"><MessageSquare className="w-4 h-4 text-[var(--fcc-green)]" /></span>}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex flex-wrap gap-2">
        {suggested.slice(0, 6).map(q => (
          <button key={q} data-testid={COPILOT.suggested} onClick={() => send(q)} className="text-sm bg-white border border-[var(--fcc-border)] rounded-full px-4 py-2 text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]">
            {q}
          </button>
        ))}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2">
        <input data-testid={COPILOT.input} value={input} onChange={e => setInput(e.target.value)} placeholder="Ask Copilot about today, tomorrow, soccer, camp, forms…" className="fcc-input" />
        <button data-testid={COPILOT.send} disabled={streaming} type="submit" className="fcc-btn-primary px-5"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
